<template>
  <div
    class="project-contractor-fields"
  >
    <div class="contractor-info-fields clearfix">
      <div
        class="contractor-type"
        :class="{'required-error-highlight': isEditing && inputRequireHighlight && !deep(contractor, 'type')}"
      >
        <label
          :class="{'required-flag': isEditing}"
          class="emphasis contractor-title"
        >
          {{ $t('tenantProjects.contractorType') }}
        </label>
        <HKLSelect
          v-if="isEditing && canEdit"
          class="search-company-type"
          :value="contractor.type || undefined"
          :options="typeHklOptions"
          :showSearch="true"
          :disabled="isMain || !isEditing || !canEdit"
          placeholder=""
          @change="val => updateField(['type'], val)"
        />
        <span
          v-else
          class=""
        >
          {{ contractorTypeView[contractor.type] || $t('emptyValue') }}
        </span>
      </div>

      <div
        class="contractor-name"
        :class="{'required-error-highlight': isEditing && inputRequireHighlight && !companyName.trim()}"
      >
        <label
          :class="{'required-flag': isEditing}"
          class="emphasis contractor-title"
        >
          {{ $t('tenancy.companyName') }}
        </label>
        <HKLSelect
          v-if="isEditing && canEdit"
          v-hkldinput
          class="search-company-name"
          :disabled="(isMain && !isTms) || !isEditing || !canEdit"
          :value="companyName || undefined"
          :options="companyHklOptions"
          :showSearch="true"
          :showArrow="false"
          :singleTaggableInput="true"
          placeholder=""
          @search="onCompanySearch"
          @input="onCompanyInput"
          @change="onCompanyChange"
        />
        <span
          v-else
          class=""
        >
          {{ companyName || $t('emptyValue') }}
        </span>
      </div>

      <div
        v-if="!isMain && isEditing && canEdit"
        class="icon-remove align-right"
      >
        <i
          class="fa fa-times-thin"
          @click="openDeleteConfirm(contractorIndex)"
        />
      </div>
    </div>

    <div
      v-show="isOther && (contractor.typeDesc || (isEditing && canEdit))"
      class="contractor-other-type clearfix"
      :class="{'edit-box-margin': (isEditing && canEdit)}"
    >
      <TextField
        v-hkldinput
        :value="contractor.typeDesc"
        :readonly="!isEditing || !canEdit"
        :placeholder="$t('tenantProjects.hintContractorDesc')"
        @updateValue="val => updateField(['typeDesc'], val.trim())"
      />
    </div>
    <div v-if="enableWorkPermitsFeature">
      <label class="primary-contact-title contractor-title">{{ $t('contact.primary') }}</label>
      <i
        class="gray_font"
        v-if="isMain || !!contractorEmail"
      >
        <span>{{ getHintMsg() }}</span>
      </i>
      <div class="tenant-contractor-admin">
        <ContractorContact
          :isEditing="isEditing && canEdit"
          :contacts="[deep(contractor, 'primaryContact')]"
          :canInvite="canEdit && !!contractorEmail"
          :invite="invite"
          :updatePath="['primaryContact']"
          :isPrimary="true"
          :update="updateContact"
          :inputRequireHighlight="inputRequireHighlight"
          :isSubFromExternal="isSubFromExternal"
        />
      </div>
    </div>

    <template v-if="isEditing || deep(contractor, 'contacts.length')">
      <label class="other-contact-title contractor-title">
        <span>{{ $t('contact.other') }}</span>
      </label>
      <i class="gray_font">
        <span>{{ $t('contact.othersHint') }}</span>
      </i>
    </template>
    <div class="tenant-contractor-contact">
      <ContractorContact
        :isEditing="isEditing && (canEdit || isCurrentContractorPrimaryContact)"
        :contacts="contractor.contacts"
        :invite="invite"
        :updatePath="['contacts']"
        :isPrimary="false"
        :update="updateContact"
        :toggleContact="toggleContact"
        :inputRequireHighlight="inputRequireHighlight"
      />
    </div>
    <HkldDropzone
      v-if="canEditDocs && canViewDocs"
      :sourceId="contractor.id || ''"
    />
    <EditAttachments
      v-if="canViewDocs"
      :sourceId="deep(contractor, 'id') || ''"
      :canDelete="canEditDocs"
      :canEdit="canEditDocs"
      :showEmptyMsg="true"
      :emptyMsg="$t('tenantProjects.contractorEmptyDocHint')"
    />
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="$t('deleteConfirmMsg')"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { unref, computed, getCurrentInstance } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import EditAttachments from '@/controls/EditAttachments'
import HkldDropzone from '@/controls/HkldDropzone'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import TextField from '@/controls/TextField'
import * as companiesApi from '@/api/companies/request'
import formFullnameMixin from '@/pages/formFullnameMixin'
import { useRouteParamId } from '@/router/useRouter'
import { useStoreGetters, useStoreActions } from '@/store'
import mutationTypes from '@/store/mutation-types'
import dateUtil from '@/utils/dateUtil'
import { companyType } from '@/utils/locale'
import { disableTenantContractorEdit } from '@/modules/workPermits/utils'
import ContractorContact from './ContractorContact'
import { CONTRACTOR_TYPES, ContactExample, getContractorTypeOptions, getContractorTypes } from './consts'
import { inviteProjectContractors } from './contractors.utils'
import { getUpdatorName } from './utils'

export default {
  name: 'ContractorFields',
  components: {
    ActionConfirmModal,
    ContractorContact,
    EditAttachments,
    HkldDropzone,
    HKLSelect,
    TextField,
  },
  mixins: [formFullnameMixin],
  props: {
    canEdit: Boolean,
    isEditing: Boolean,
    isTenantProject: Boolean,
    contractor: {
      type: Object,
      default () { return {} },
    },
    contractorIndex: {
      type: Number,
      default: -1,
    },
    inputRequireHighlight: Boolean,
  },
  setup (props) {
    const { proxy } = getCurrentInstance()

    const projectContractorStore = useStoreGetters('projectContractors', ['isTms'])

    const projectId = useRouteParamId()
    const { loadProject } = useStoreActions('projects', ['loadProject'])
    const { loadTenantProject } = useStoreActions('tenantProject', ['loadTenantProject'])
    async function invite () {
      const { isTms } = projectContractorStore
      await inviteProjectContractors(isTms, projectId, props.contractor)
      if (unref(isTms)) {
        await loadTenantProject(unref(projectId))
      } else {
        await loadProject(unref(projectId))
      }
    }
    const isSubFromExternal = computed(() => {
      return !proxy.isMain && disableTenantContractorEdit()
    })

    const contractorEmail = computed(() => {
      return lodash.get(proxy.contractor, 'primaryContact.email', '')
    })

    return {
      invite,
      isSubFromExternal,
      contractorEmail,
    }
  },
  data () {
    const itemHeaders = [{
      name: this.$t('name'),
    }, {
      name: this.$t('officeTel'),
    }, {
      name: this.$t('mobile'),
    }, {
      name: this.$t('contact.email.emailAddr'),
    }]
    return {
      delConfModalId: `contractorField-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      deep,
      searchTimeout: 500,
      itemHeaders,
      searchCompaniesResult: [],
      companyNameSearchText: '',
    }
  },
  computed: {
    ...mapGetters('tenantProject', {
      accounts: 'accountsReturnEmptyListIfEmpty',
      currentTenantProject: 'currentTenantProject',
      isCompleted: 'isCompleted',
      isWithdrawn: 'isWithdrawn' }),
    ...mapGetters('projectContractors', ['currentMainContractor', 'currentContractors', 'isMainUser', 'isTms', 'isSubUser', 'isCurrentVendorAccount']),
    ...mapGetters('ccd', {
      canAccessAllProjects: 'canAccessAllProjects',
      tenancyAccounts: 'accounts'}),
    ...mapGetters('user', ['currentUser', 'isEngineer']),
    ...mapGetters('regions', ['enableWorkPermitsFeature']),
    isMain () {
      return this.contractor.type === CONTRACTOR_TYPES.MAIN
    },
    contractorTypeView () {
      return getContractorTypes()
    },
    canEditDocs () {
      if (this.canEdit) {
        return true
      }
      return this.canSubUserEditDocs || this.isMainUser || this.isCurrentVendorAccount
    },
    canViewDocs () {
      const isRepresentative = this.accounts.some((account) => account.id === this.currentUser.id)
      if (isRepresentative) {
        return true
      }
      return this.canEditDocs || this.isEngineer
    },
    canSubUserEditDocs () {
      if (this.isWithdrawn || this.isCompleted) {
        return false
      }
      return this.isSubUser && this.isCurrentContractorPrimaryContact
    },
    isOther () {
      return this.contractor.type === CONTRACTOR_TYPES.OTHER
    },
    companyName () {
      return lodash.get(this.contractor, 'company.name') || ''
    },
    companyNames () {
      return this.searchCompaniesResult.map(company => company.name)
    },
    selectedType () {
      return lodash.get(this.contractor, 'type') ? getContractorTypeOptions().filter(option => option.code === this.contractor.type)[0] : {}
    },
    typeOptions () {
      return getContractorTypeOptions().slice(1)
    },
    typeHklOptions () {
      const selectedType = this.selectedType && this.selectedType.code ? this.selectedType : null
      const options = (this.typeOptions || []).slice()
      if (selectedType && !options.some(option => option.code === selectedType.code)) {
        options.unshift(selectedType)
      }
      return options.map((opt) => ({ name: opt.label, value: opt.code }))
    },
    companyHklOptions () {
      const duplicatedMap = {}
      const uniqueCompanyNames = (this.searchCompaniesResult || []).reduce((acc, company) => {
        const companyName = this.normalizeCompanyName(company && company.name)
        if (!companyName) return acc
        const normalizedName = this.normalizeDuplicateKey(companyName)
        if (duplicatedMap[normalizedName]) return acc
        duplicatedMap[normalizedName] = true
        acc.push(companyName)
        return acc
      }, [])
      const temporaryCompanyNameOption = this.getTemporaryCompanyNameOption()
      const companyOptions = uniqueCompanyNames.map((name) => ({ name, value: name }))
      return temporaryCompanyNameOption
        ? [temporaryCompanyNameOption, ...companyOptions]
        : companyOptions
    },
    inviterName () {
      return getUpdatorName(lodash.get(this.contractor, 'primaryContact.inviter') || {}, this.currentMainContractor, this.tenancyAccounts)
    },
    invitedTime () {
      const invitedAt = lodash.get(this.contractor, 'primaryContact.invitedAt')
      return invitedAt ? dateUtil.format(invitedAt, 'DD MMM YY HH:mm') : ''
    },
    isCurrentContractorPrimaryContact () {
      return this.currentUser.id === lodash.get(this.contractor, 'primaryContact.userId')
    },
  },
  methods: {
    ...mapMutations('projectContractors', {
      updateContractorField: mutationTypes.UPDATE_PATH_FIELD,
    }),
    ...mapActions('projects', ['loadProject']),
    ...mapActions('tenantProject', ['loadTenantProject']),
    updateField (updatePath, newValue) {
      const contractorPath = this.isMain ? ['mainContractor'] : ['contractors', this.contractorIndex]
      const path = contractorPath.concat(updatePath)
      if (path[0] === 'mainContractor' && path[1] === 'primaryContact' && !this.contractor.primaryContact) {
        this.updateContractorField({ path: path.slice(0, 2), newValue: {} })
      }
      this.updateContractorField({ path, newValue })
    },
    onCompanySearch (query) {
      this.companyNameSearchText = String(query || '').trim()
      this.searchCompanies(this.companyNameSearchText)
    },
    onCompanyChange (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      if (!name) {
        this.companyNameSearchText = ''
        this.selectedCompany = {}
        this.changeSelect()
        return
      }
      this.companyNameSearchText = ''
      const normalizedName = this.normalizeDuplicateKey(name)
      const existing = this.searchCompaniesResult.find((company) =>
        this.normalizeDuplicateKey(company && company.name) === normalizedName,
      )
      if (existing) {
        this.selectCompanyName(existing.name, this.contractorIndex)
      } else {
        this.addContractorCompany(name)
      }
    },
    onCompanyInput (val) {
      const name = Array.isArray(val) ? val[val.length - 1] : val
      this.selectedCompany = name
        ? { name, type: companyType.PC }
        : {}
      this.changeSelect()
    },
    async searchCompanies (name) {
      this.searchCompaniesResult = await companiesApi.searchCompanies({
        name, type: companyType.C, active: true,
      })
    },
    changeSelect () {
      this.updateField(['company'], this.selectedCompany)
      this.updateField(['companyId'], this.selectedCompany.id || '')
    },
    selectCompanyName (name) {
      const normalizedName = this.normalizeDuplicateKey(name)
      this.selectedCompany = this.searchCompaniesResult.find(company =>
        this.normalizeDuplicateKey(company && company.name) === normalizedName,
      )
      this.changeSelect()
    },
    addContractorCompany (companyName) {
      this.selectedCompany = {
        name: this.normalizeCompanyName(companyName),
        type: companyType.PC,
      }
      this.changeSelect()
    },
    normalizeCompanyName (name) {
      return String(name || '')
        .normalize('NFKC')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    normalizeDuplicateKey (name) {
      return this.normalizeCompanyName(name).toLowerCase()
    },
    getTemporaryCompanyNameOption () {
      const searchText = String(this.companyNameSearchText || '').trim()
      if (!searchText) return null

      const normalizedSearchText = this.normalizeDuplicateKey(searchText)
      const hasExisting = (this.searchCompaniesResult || []).some((company) =>
        this.normalizeDuplicateKey(company && company.name) === normalizedSearchText,
      )
      if (hasExisting) return null

      return {
        name: searchText,
        value: searchText,
        isSingleTaggableInputTag: true,
        hklSelectHint: this.$t('hint.addTempCompany'),
      }
    },
    updateContact (updatePath, attr, val) {
      this.updateField(updatePath.concat(attr), val)
    },
    toggleContact (index = 0, isDelete) {
      if (isDelete) {
        const contactList = lodash.cloneDeep(this.contractor.contacts)
        contactList.splice(index, 1)
        this.updateField(['contacts'], contactList)
      } else if (index) {
        this.updateContact(['contacts'], index, lodash.cloneDeep(ContactExample))
      } else {
        this.updateField(['contacts'], [lodash.cloneDeep(ContactExample)])
      }
    },
    removeContractor (index) {
      const contractorList = lodash.cloneDeep(this.currentContractors)
      contractorList.splice(index, 1)
      this.updateContractorField({ path: ['contractors'], newValue: contractorList })
    },
    getHintMsg () {
      return this.invitedTime ? this.$t('invitedByOn', [this.inviterName, this.invitedTime]) : this.$t('contact.primaryHint')
    },
    openDeleteConfirm (contractorIndex) {
      this.$refs.deleteConfirmModal.open(this.removeContractor, [contractorIndex])
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';
@import '~styles/icon_remove';

.project-contractor-fields {
  padding: 10px 15px;
  box-shadow: 0 1px 5px rgba(128, 128, 128, 0.2);

  .align-right {
    float: right;
  }

  .contractor-title {
    font-size: 16px;
  }

  .contractor-type {
    width: 295px;
    margin-right: 40px;
  }
  .contractor-other-type {
    width: 880px;
  }
  .contractor-other-type.edit-box-margin {
    margin-top: 12px;
  }
  .contractor-name {
    width: 500px;
  }
  .contractor-type, .contractor-name {
    float: left;
  }

  .gray_font {
    font-size: 12px;
    color: #878787;
    display: inline-block;
    height: 20px;
    margin-bottom: 5px;
  }

  .icon-remove {
    .fa.fa-times-thin::before {
      font-size: 32px;
      padding: 0;
    }
  }

  .circle-add {
    vertical-align: bottom;
    margin-left: 5px;
    display: inline-block;
    height: 20px;
    width: 20px;
    cursor: pointer;
    background: url('../../../../static/plus.svg') center center no-repeat;
  }
}
</style>

<style lang="scss">
.project-contractor-fields {
  .contractor-name {
    .multiselect.search-company-name>.multiselect__select::before {
      display: none;
    }
  }
}
</style>
